import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { api, fmtINR, fmtMeters, fmtRate, formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import { Search, FileText, Loader2, ClipboardList } from "lucide-react";

const Field = ({ label, value }) => (
  <div className="border-b border-[#E6E4DD] py-2">
    <div className="text-[10px] uppercase tracking-wider text-[#8CA3A7]">{label}</div>
    <div className="text-sm font-mono text-[#0A0D14] mt-0.5">{value ?? "—"}</div>
  </div>
);

export default function Status() {
  const [searchParams] = useSearchParams();
  const [po, setPo] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const runLoad = async (poVal) => {
    const v = (poVal || "").trim();
    if (!v) { toast.error("Enter a P.O. number."); return; }
    setLoading(true);
    try {
      const { data } = await api.get("/status", { params: { po: v } });
      setData(data);
    } catch (err) {
      setData(null);
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Not found.");
    } finally { setLoading(false); }
  };

  const load = (e) => { e?.preventDefault(); runLoad(po); };

  useEffect(() => {
    const initial = searchParams.get("po");
    if (initial) { setPo(initial); runLoad(initial); }
    // eslint-disable-next-line
  }, [searchParams]);

  const downloadPdf = async () => {
    try {
      const res = await api.get("/status/pdf", { params: { po: data.po_number }, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `status-${data.po_number}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Could not generate PDF."); }
  };

  return (
    <div className="space-y-6" data-testid="status-page">
      <div>
        <p className="text-xs font-medium uppercase tracking-[0.2em] text-[#8CA3A7]">Order Status</p>
        <h1 className="font-heading text-4xl font-bold text-[#0A0D14] tracking-tight mt-1">Status</h1>
        <p className="text-sm text-[#8CA3A7] mt-2">Type a Purchase Order number to see its full dispatch & amount status.</p>
      </div>

      <form onSubmit={load} className="flex items-center gap-2 max-w-xl">
        <div className="relative flex-1">
          <Search className="w-4 h-4 text-[#8CA3A7] absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            data-testid="status-po-input"
            value={po}
            onChange={(e) => setPo(e.target.value)}
            placeholder="e.g. MDLLP/26-27/F00013"
            className="w-full rounded-sm border border-[#E6E4DD] bg-white pl-9 pr-3 py-2.5 text-sm font-mono focus:outline-none focus:ring-1 focus:ring-[#1C2331]"
          />
        </div>
        <button data-testid="status-load-btn" type="submit" disabled={loading} className="bg-[#1C2331] text-white rounded-sm px-4 py-2.5 text-sm font-medium hover:bg-[#2A3447] transition-colors disabled:opacity-60 flex items-center gap-2">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />} Load
        </button>
      </form>

      {!data ? (
        <div className="bg-white border border-[#E6E4DD] rounded-sm p-16 text-center">
          <ClipboardList className="w-8 h-8 text-[#8CA3A7] mx-auto mb-3" />
          <p className="text-sm text-[#4B5563]">Enter a P.O. number above to view its status.</p>
        </div>
      ) : (
        <div className="space-y-6" data-testid="status-result">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-mono text-sm text-[#D95D39]">{data.po_number}</p>
              <h2 className="font-heading text-2xl font-bold text-[#0A0D14] mt-0.5">{data.buyer_name} <span className="text-[#8CA3A7] font-normal">from</span> {data.seller_name}</h2>
            </div>
            <button data-testid="status-pdf-btn" onClick={downloadPdf} className="text-sm bg-white border border-[#E6E4DD] rounded-sm px-3 py-2 font-medium flex items-center gap-1.5 hover:border-[#1C2331] transition-colors">
              <FileText className="w-3.5 h-3.5" /> Download PDF
            </button>
          </div>

          <div className="bg-white border border-[#E6E4DD] rounded-sm p-6 grid grid-cols-2 md:grid-cols-4 gap-x-8">
            <Field label="Category Seller" value={data.category_seller || "-"} />
            <Field label="Fabric" value={data.fabric_type} />
            <Field label="Rate" value={`${fmtRate(data.rate)}/m`} />
            <Field label="Payment Term" value={`${data.payment_terms_days} days`} />
            <Field label="Order Quantity" value={fmtMeters(data.order_quantity)} />
            <Field label="Delivered" value={fmtMeters(data.delivered)} />
            <Field label="Pending" value={fmtMeters(data.pending)} />
            <Field label="Total Amount (incl GST)" value={fmtINR(data.total_amount)} />
            <Field label="Est. Delivery Date" value={data.est_delivery_date || "-"} />
            <Field label="Actual Delivery Date" value={data.actual_delivery_date || "-"} />
          </div>

          <div className="border border-[#E6E4DD] rounded-sm bg-white overflow-hidden">
            <table className="w-full text-sm text-left">
              <thead>
                <tr className="font-mono text-[10px] uppercase tracking-wider text-[#8CA3A7] bg-slate-50 border-b border-[#E6E4DD]">
                  <th className="px-3 py-3">Dispatch #</th>
                  <th className="px-3 py-3">Date</th>
                  <th className="px-3 py-3 text-right">Qty</th>
                  <th className="px-3 py-3">GR Number</th>
                  <th className="px-3 py-3">Transport</th>
                  <th className="px-3 py-3">Mode</th>
                  <th className="px-3 py-3">Payment Due On</th>
                  <th className="px-3 py-3 text-right">Amount</th>
                </tr>
              </thead>
              <tbody>
                {data.dispatches.map((d, i) => (
                  <tr key={i} data-testid={`status-dispatch-${i}`} className="border-b border-slate-100 last:border-0">
                    <td className="px-3 py-2 font-mono text-[#0A0D14]">{d.dispatch_number || "-"}</td>
                    <td className="px-3 py-2 text-[#4B5563]">{d.dispatch_date || "-"}</td>
                    <td className="px-3 py-2 text-right font-mono">{fmtMeters(d.qty)}</td>
                    <td className="px-3 py-2 text-[#4B5563] font-mono text-xs">{d.gr_number || "-"}</td>
                    <td className="px-3 py-2 text-[#4B5563]">{d.transport || "-"}</td>
                    <td className="px-3 py-2 text-[#4B5563]">{d.mode || "-"}</td>
                    <td className="px-3 py-2 text-[#4B5563]">{d.payment_due_on || "-"}</td>
                    <td className="px-3 py-2 text-right font-mono text-[#0A0D14]">{fmtINR(d.amount)}</td>
                  </tr>
                ))}
                {data.dispatches.length === 0 && (
                  <tr><td colSpan={8} className="px-3 py-8 text-center text-sm text-slate-400">No dispatches recorded.</td></tr>
                )}
                <tr className="bg-slate-50 font-semibold">
                  <td className="px-3 py-2">Total</td>
                  <td></td>
                  <td className="px-3 py-2 text-right font-mono">{fmtMeters(data.delivered)}</td>
                  <td colSpan={4}></td>
                  <td className="px-3 py-2 text-right font-mono text-[#0A0D14]">{fmtINR(data.total_amount)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
